//# sourceMappingURL=murmur3a.mjs.map
/// <reference types="./murmur3a.d.mts" />
import * as $bit_array from "../gleam_stdlib/gleam/bit_array.mjs";
import * as $int from "../gleam_stdlib/gleam/int.mjs";
import {
  bitwise_and as and,
  bitwise_exclusive_or as xor,
  bitwise_or as or,
  bitwise_shift_left as shift_left,
  bitwise_shift_right as shift_right,
} from "../gleam_stdlib/gleam/int.mjs";
import * as $list from "../gleam_stdlib/gleam/list.mjs";
import * as $result from "../gleam_stdlib/gleam/result.mjs";
import {
  Ok,
  Error,
  CustomType as $CustomType,
  makeError,
  toBitArray,
  bitArraySlice,
  sizedInt,
  stringBits,
} from "./gleam.mjs";
import { signed_multiply } from "./murmur3a_ffi.mjs";

const FILEPATH = "src/murmur3a.gleam";

class Hash extends $CustomType {
  constructor(seed, shift, state) {
    super();
    this.seed = seed;
    this.shift = shift;
    this.state = state;
  }
}

const int32_modulus = 0x100000000;

const int32_max = 0x7fffffff;

const m1 = 0xffffffff;

const c2 = 0x1b873593;

const c1 = 0xcc9e2d51;

const default$ = /* @__PURE__ */ new Hash(0, 0, 0);

function rotate_left(n, shift) {
  let n$1 = and(n, m1);
  let _pipe = shift_left(n$1, shift);
  let _pipe$1 = and(_pipe, m1);
  return or(_pipe$1, shift_right(n$1, 32 - shift));
}

function finalize(hash, length) {
  let _block;
  let _block$1;
  let $ = hash.state;
  if ($ === 0) {
    _block$1 = hash.seed;
  } else {
    let _pipe = hash.state;
    let _pipe$1 = signed_multiply(_pipe, c1);
    let _pipe$2 = rotate_left(_pipe$1, 15);
    let _pipe$3 = signed_multiply(_pipe$2, c2);
    let _pipe$4 = xor(_pipe$3, hash.seed);
    _block$1 = and(_pipe$4, m1);
  }
  let _pipe = _block$1;
  _block = xor(_pipe, length);
  let state = _block;
  let _block$2;
  let _pipe$1 = shift_right(state, 16);
  let _pipe$2 = xor(_pipe$1, state);
  let _pipe$3 = signed_multiply(_pipe$2, 0x85ebca6b);
  _block$2 = and(_pipe$3, m1);
  let state$1 = _block$2;
  let _block$3;
  let _pipe$4 = shift_right(state$1, 13);
  let _pipe$5 = xor(_pipe$4, state$1);
  _block$3 = signed_multiply(_pipe$5, 0xc2b2ae35);
  let state$2 = _block$3;
  return new Hash(
    hash.seed,
    hash.shift,
    (() => {
      let _pipe$6 = shift_right(state$2, 16);
      let _pipe$7 = and(_pipe$6, 0xffff);
      return xor(_pipe$7, state$2);
    })(),
  );
}

function mix(state, seed) {
  let _pipe = state;
  let _pipe$1 = signed_multiply(_pipe, c1);
  let _pipe$2 = rotate_left(_pipe$1, 15);
  let _pipe$3 = signed_multiply(_pipe$2, c2);
  let _pipe$4 = xor(_pipe$3, seed);
  let _pipe$5 = rotate_left(_pipe$4, 13);
  let _pipe$6 = signed_multiply(_pipe$5, 5);
  return $int.add(_pipe$6, 0xe6546b64);
}

function hash_byte(hash, byte) {
  let _block;
  let _pipe = byte;
  let _pipe$1 = shift_left(_pipe, hash.shift);
  _block = or(_pipe$1, hash.state);
  let state = _block;
  let $ = hash.shift;
  if ($ === 24) {
    return new Hash(mix(state, hash.seed), 0, 0);
  } else {
    return new Hash(hash.seed, hash.shift + 8, state);
  }
}

function do_hash_bytes(loop$hash, loop$key) {
  while (true) {
    let hash = loop$hash;
    let key = loop$key;
    if (key.bitSize >= 8) {
      if (key.bitSize >= 16 && key.bitSize >= 24 && key.bitSize >= 32) {
        if (key.bitSize % 8 === 0) {
          let first = key.byteAt(0);
          let second = key.byteAt(1);
          let third = key.byteAt(2);
          let fourth = key.byteAt(3);
          let rest = bitArraySlice(key, 32);
          let _block;
          let _pipe = first;
          let _pipe$1 = or(_pipe, shift_left(second, 8));
          let _pipe$2 = or(_pipe$1, shift_left(third, 16));
          let _pipe$3 = or(_pipe$2, shift_left(fourth, 24));
          _block = mix(_pipe$3, hash.seed);
          let seed = _block;
          loop$hash = new Hash(seed, hash.shift, hash.state);
          loop$key = rest;
        } else {
          return new Error(undefined);
        }
      } else if (key.bitSize % 8 === 0) {
        let byte = key.byteAt(0);
        let rest = bitArraySlice(key, 8);
        let _pipe = hash_byte(hash, byte);
        loop$hash = _pipe;
        loop$key = rest;
      } else {
        return new Error(undefined);
      }
    } else if (key.bitSize === 0) {
      return new Ok(hash);
    } else {
      return new Error(undefined);
    }
  }
}

/**
 * Computes a 32 bit Murmur3 hash of the supplied bit array.
 *
 * Returns `Error(Nil)` if the bit array is not byte-aligned.
 *
 * ## Examples
 *
 * ```gleam
 * hash_bytes(<<"murmur3a">>, 0)
 * // -> Ok(Hash(3521997819, 0, -391047908))
 * ```
 */
export function hash_bytes(key, seed) {
  let _pipe = do_hash_bytes(new Hash(seed, default$.shift, default$.state), key);
  return $result.map(
    _pipe,
    (_capture) => { return finalize(_capture, $bit_array.byte_size(key)); },
  );
}

/**
 * Computes a 32 bit Murmur3 hash of the input string.
 *
 * ## Examples
 *
 * ```gleam
 * hash_string("murmur3a", 0)
 * // -> Hash(3521997819, 0, -391047908)
 * ```
 */
export function hash_string(key, seed) {
  let $ = hash_bytes(toBitArray([stringBits(key)]), seed);
  let hash;
  if ($ instanceof Ok) {
    hash = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "murmur3a",
      33,
      "hash_string",
      "Pattern match failed, no pattern matched the value.",
      { value: $, start: 755, end: 807, pattern_start: 766, pattern_end: 774 }
    )
  }
  return hash;
}

/**
 * Computes a 32 bit Murmur3 hash of the supplied list of integers.
 *
 * **Note:** Values outside the 0–255 range will be truncated to their lowest
 * byte before hashing.
 *
 * ## Examples
 *
 * ```gleam
 * hash_ints([1, 2, 3], 0)
 * // -> Hash(0, 24, -2133732860)
 * ```
 */
export function hash_ints(key, seed) {
  let _block;
  let _pipe = key;
  _block = $list.fold(
    _pipe,
    [new Hash(seed, default$.shift, default$.state), 0],
    (acc, i) => {
      let hash = acc[0];
      let length = acc[1];
      return [hash_byte(hash, and(i, 0xff)), length + 1];
    },
  );
  let $ = _block;
  let hash = $[0];
  let length = $[1];
  return finalize(hash, length);
}

/**
 * Returns the 32 bit _signed_ integer digest of the hash.
 *
 * Keep in mind that these values may return negative numbers!
 *
 * ## Examples
 *
 * ```gleam
 * hash_ints([1, 2, 3], 0)
 * |> int_digest
 * // -> -2133732860
 * ```
 */
export function int_digest(hash) {
  return hash.state;
}

/**
 * Returns 32 bit digest of the hash as a BitArray.
 *
 * ## Examples
 *
 * ```gleam
 * hash_string("murmur3a", 0)
 * |> bit_array_digest
 * // -> <<232, 177, 21, 28>>
 * ```
 */
export function bit_array_digest(hash) {
  return toBitArray([sizedInt(int_digest(hash), 32, true)]);
}

/**
 * Returns a hexadecimal digest of the hash.
 *
 * ## Examples
 *
 * ```gleam
 * hash_string("murmur3a", 0)
 * |> hex_digest
 * // -> "E8B1151C"
 * ```
 */
export function hex_digest(hash) {
  let _pipe = hash;
  let _pipe$1 = bit_array_digest(_pipe);
  return $bit_array.base16_encode(_pipe$1);
}
